import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import { Modal as AModal, DatePicker, DatePickerProps, TimePicker } from 'antd'
import { RangePickerProps } from 'antd/es/date-picker'
import dayjs, { Dayjs } from 'dayjs'
import moment from 'moment'
import Modal from '@common/Modal'
import classes from './index.module.css'
import sharedClasses from '../index.module.css'
import jclasses from '@pages/Admin/CRM/styles/jsmodal.module.css'
import lessonReportsClasses from '@pages/Main/LessonReports/index.module.css'
import LessonReportCard from '@pages/Main/LessonReports/components/LessonReportCard/LessonReportCard'
import {
  fetchTimetableReports,
  makeLessonCancel,
  makeLessonCompleted,
  rescheduleIntroductoryLesson,
  rescheduleLesson,
} from '@pages/Main/LessonReports/utils/api'
import { ITimetableReportItem } from '@pages/Journal2/components/Tabs/KTPJournal'
import TextAreaField from '@pages/Admin/CourseSettings/components/TextAreaField/TextAreaField'
import FieldWrapper from '@pages/Admin/CourseSettings/components/FieldWrapper/FieldWrapper'
import ChangeStudentsLessonStatusModal from '@pages/Main/LessonReports/modals/SaveStudentsLessonReportModal/ChangeStudentsLessonStatusModal/SaveStudentsLessonReportModal'
import { useAddAlert } from '@/hooks/useAddAlert'
import useRoles from '@/hooks/useRoles'

import { isTeacherLessonReportJournalRestrictionApplicable } from '@/helpers/teacherLessonReportJournalScope'
const STATUS_DONE = '5'
const STATUS_RESCHEDULE = 'reschedule'
const STATUS_GROUP = 'group'
const STATUS_INTRODUCTION = 'introduction'
const STATUS_UNKNOWN = '0'
const INITIAL_REPORTS_LIMIT = 5
const INITIAL_REPORTS_PAGE = 1

const getStatusDescription = (value: string) => {
  switch (value) {
    case STATUS_DONE:
      return 'Напишите комментарий'
    case STATUS_RESCHEDULE:
      return 'Напишите комментарий (перенос занятия)'
    case '10':
      return 'Опишите причину отмены занятия (отмена учеником)'
    case '11':
      return 'Опишите причину отмены занятия (отмена педагогом)'
    case '12':
      return 'Опишите причину отмены занятия (платная отмена)'
    case '14':
      return 'Опишите причину отмены занятия (не оплачено)'
    default:
      return 'Напишите комментарий'
  }
}

const normalizeMinutes = (value: unknown, fallback = 15) => {
  const parsedValue = Number(value)

  return Number.isFinite(parsedValue) ? parsedValue : fallback
}

const isEnabledSetting = (value: unknown) => String(value) === '1'

function SaveContentModal({
  report,
  status,
  setComment,
  setSchedule,
  setStartedAt,
  setEndedAt,
}: {
  report: ITimetableReportItem
  status: string
  setComment: (value: string) => void
  setSchedule: (value: Dayjs) => void
  setStartedAt: (value: Dayjs) => void
  setEndedAt: (value: Dayjs) => void
}) {
  const [form, setForm] = useState({
    comment: '',
    date: dayjs().add(1, 'day'),
    startedAt: dayjs(report.start_time, 'HH:mm'),
    endedAt: dayjs(report.end_time, 'HH:mm'),
  })

  const onChangeDate: DatePickerProps['onChange'] = (date) => {
    if (date) {
      setSchedule(date)
      setForm((prev) => {
        return { ...prev, date }
      })
    }
  }

  const onChangeTimes: RangePickerProps['onChange'] = (times) => {
    if (!times) return

    const [start, end] = times

    if (!start || !end) {
      return
    }

    setStartedAt(start)
    setEndedAt(end)
    setForm((prev) => {
      return { ...prev, startedAt: start, endedAt: end }
    })
  }

  return (
    <div
      className={`${lessonReportsClasses.modalBody} ${classes.confirmContent}`}
    >
      <TextAreaField
        className={lessonReportsClasses.textarea}
        label={getStatusDescription(status)}
        placeholder="комментарий"
        value={form.comment}
        onChange={(value) => {
          setForm((prev) => {
            return { ...prev, comment: value.target.value }
          })
          setComment(value?.target?.value)
        }}
        autoSize={{ minRows: 3, maxRows: 6 }}
      />
      {status === STATUS_RESCHEDULE && (
        <>
          <FieldWrapper label="Дата занятия">
            <DatePicker
              className={lessonReportsClasses.dateTimePicker}
              value={form.date}
              format="DD.MM.YYYY"
              allowClear={false}
              onChange={onChangeDate}
              disabledDate={(current) => {
                return current && current < dayjs().startOf('day')
              }}
            />
          </FieldWrapper>
          <FieldWrapper label="Начало / конец занятия">
            <TimePicker.RangePicker
              className={lessonReportsClasses.dateTimePicker}
              value={
                form.startedAt && form.endedAt
                  ? [
                      dayjs(form.startedAt, 'HH:mm'),
                      dayjs(form.endedAt, 'HH:mm'),
                    ]
                  : null
              }
              onChange={onChangeTimes}
              format="HH:mm"
              order={false}
              clearIcon={false}
            />
          </FieldWrapper>
        </>
      )}
    </div>
  )
}

const RequiredLessonReportModal = () => {
  const location = useLocation()
  const addAlert = useAddAlert()
  const { isSeminarian } = useRoles()
  const platformTimeState = useSelector((store: any) => store.platformTime)
  const settings = useSelector((store: any) => store.settings)
  const [groupReportsQueue, setGroupReportsQueue] = useState<
    ITimetableReportItem[]
  >([])
  const [individualReportsQueue, setIndividualReportsQueue] = useState<
    ITimetableReportItem[]
  >([])
  const [introductionReportsQueue, setIntroductionReportsQueue] = useState<
    ITimetableReportItem[]
  >([])
  const [reportDrafts, setReportDrafts] = useState<
    Record<
      number,
      {
        duration: number
        opened_at: string | null
        status: string | null
      }
    >
  >({})
  const [studentsStatusModal, setStudentsStatusModal] = useState<{
    report: ITimetableReportItem
    status: string
  } | null>(null)
  const fetchInProgressRef = useRef(false)
  const processedReportsIdsRef = useRef<Set<number>>(new Set())

  const groupPopupEnabled =
    isEnabledSetting(settings?.isShowRequiredPopupIfTeacherDidNotReportGroupLesson) &&
    !isEnabledSetting(settings?.autoMarkGroupLessonsAsDone)
  const individualPopupEnabled =
    isEnabledSetting(
      settings?.isShowRequiredPopupIfTeacherDidNotReportIndividualLesson,
    ) && !isEnabledSetting(settings?.autoMarkIndividualLessonsAsDone)
  // Обязательный поп-ап по вводному занятию — отдельная настройка платформы
  const introductionPopupEnabled = isEnabledSetting(
    settings?.isIntroductoryLessonReportPopup,
  )
  const isJournalPage = location.pathname.startsWith('/v2/journal')

  const canUseRequiredReportModal =
    isSeminarian &&
    !isJournalPage &&
    (groupPopupEnabled || individualPopupEnabled || introductionPopupEnabled)
  const canCheckReports =
    canUseRequiredReportModal && platformTimeState?.status === 'success'

  const platformTimeMinuteKey =
    platformTimeState?.status === 'success'
      ? moment(platformTimeState?.platformTime).format('YYYY-MM-DD HH:mm')
      : ''
  const currentReport =
    groupReportsQueue[0] ??
    individualReportsQueue[0] ??
    introductionReportsQueue[0]

  const isGroupReportBlockedByGrades = useCallback(
    (report: ITimetableReportItem) => {
      return (
        isTeacherLessonReportJournalRestrictionApplicable(settings, report) &&
        report?.conference_type === STATUS_GROUP &&
        report?.direction_tariff !== 'group_per_lesson' &&
        !report?.all_children_graded
      )
    },
    [settings],
  )

  const isReportReadyForRequiredPopup = useCallback(
    (report: ITimetableReportItem) => {
      if (!report || Number(report?.status) !== Number(STATUS_UNKNOWN)) {
        return false
      }

      if (processedReportsIdsRef.current.has(report.id)) {
        return false
      }

      const isGroupReport = report?.conference_type === STATUS_GROUP
      const isIntroductionReport =
        report?.conference_type === STATUS_INTRODUCTION

      if (isIntroductionReport && !introductionPopupEnabled) {
        return false
      }

      if (isGroupReport && !groupPopupEnabled) {
        return false
      }

      if (!isGroupReport && !isIntroductionReport && !individualPopupEnabled) {
        return false
      }

      if (!report?.date_conference || !report?.end_time) {
        return false
      }

      let popupDelayMinutes
      if (isIntroductionReport) {
        popupDelayMinutes = normalizeMinutes(
          settings?.introductoryLessonReportPopupMinutes,
          10,
        )
      } else if (isGroupReport) {
        popupDelayMinutes = normalizeMinutes(
          settings?.showRequiredPopupIfTeacherDidNotReportGroupLessonAfterMinutes,
        )
      } else {
        popupDelayMinutes = normalizeMinutes(
          settings?.showRequiredPopupIfTeacherDidNotReportIndividualLessonAfterMinutes,
        )
      }

      const lessonEndTime = moment(
        `${report.date_conference} ${report.end_time}`,
        'YYYY-MM-DD HH:mm',
      )

      if (!lessonEndTime.isValid()) {
        return false
      }

      return moment(platformTimeState?.platformTime).isSameOrAfter(
        lessonEndTime.add(popupDelayMinutes, 'minute'),
      )
    },
    [
      groupPopupEnabled,
      individualPopupEnabled,
      introductionPopupEnabled,
      platformTimeState?.platformTime,
      settings?.showRequiredPopupIfTeacherDidNotReportGroupLessonAfterMinutes,
      settings?.showRequiredPopupIfTeacherDidNotReportIndividualLessonAfterMinutes,
      settings?.introductoryLessonReportPopupMinutes,
    ],
  )

  useEffect(() => {
    if (!canUseRequiredReportModal) {
      setGroupReportsQueue([])
      setIndividualReportsQueue([])
      setIntroductionReportsQueue([])
      setReportDrafts({})
      return
    }

    if (!canCheckReports) {
      return
    }

    if (fetchInProgressRef.current) {
      return
    }

    const shouldLoadGroupReports =
      groupPopupEnabled && groupReportsQueue.length === 0 && !currentReport
    const shouldLoadIndividualReports =
      individualPopupEnabled &&
      individualReportsQueue.length === 0 &&
      !currentReport
    const shouldLoadIntroductionReports =
      introductionPopupEnabled &&
      introductionReportsQueue.length === 0 &&
      !currentReport

    if (
      !shouldLoadGroupReports &&
      !shouldLoadIndividualReports &&
      !shouldLoadIntroductionReports
    ) {
      return
    }

    let isMounted = true

    const loadReports = async () => {
      fetchInProgressRef.current = true

      const [groupReports, individualReports, introductionReports] =
        await Promise.all([
          shouldLoadGroupReports
            ? fetchTimetableReports(
                INITIAL_REPORTS_LIMIT,
                INITIAL_REPORTS_PAGE,
                {
                  filter: ['[conference_type][eq]=group'],
                },
              )
            : Promise.resolve(null),
          shouldLoadIndividualReports
            ? fetchTimetableReports(
                INITIAL_REPORTS_LIMIT,
                INITIAL_REPORTS_PAGE,
                {
                  filter: ['[conference_type][eq]=private'],
                },
              )
            : Promise.resolve(null),
          shouldLoadIntroductionReports
            ? fetchTimetableReports(
                INITIAL_REPORTS_LIMIT,
                INITIAL_REPORTS_PAGE,
                {
                  filter: ['[conference_type][eq]=introduction'],
                },
              )
            : Promise.resolve(null),
        ])

      if (!isMounted) {
        fetchInProgressRef.current = false
        return
      }

      if (groupReports) {
        setGroupReportsQueue(groupReports.filter(isReportReadyForRequiredPopup))
      }

      if (individualReports) {
        setIndividualReportsQueue(
          individualReports.filter(isReportReadyForRequiredPopup),
        )
      }

      if (introductionReports) {
        setIntroductionReportsQueue(
          introductionReports.filter(isReportReadyForRequiredPopup),
        )
      }

      fetchInProgressRef.current = false
    }

    loadReports()

    return () => {
      isMounted = false
    }
  }, [
    canCheckReports,
    canUseRequiredReportModal,
    groupPopupEnabled,
    groupReportsQueue.length,
    individualPopupEnabled,
    individualReportsQueue.length,
    introductionPopupEnabled,
    introductionReportsQueue.length,
    isReportReadyForRequiredPopup,
    location.pathname,
    platformTimeMinuteKey,
    currentReport,
  ])

  const removeReportFromQueue = useCallback((reportId: number) => {
    setGroupReportsQueue((prev) => prev.filter((item) => item.id !== reportId))
    setIndividualReportsQueue((prev) =>
      prev.filter((item) => item.id !== reportId),
    )
    setIntroductionReportsQueue((prev) =>
      prev.filter((item) => item.id !== reportId),
    )
    setReportDrafts((prev) => {
      if (!prev[reportId]) {
        return prev
      }

      const next = { ...prev }
      delete next[reportId]

      return next
    })
  }, [])

  const saveReport = useCallback(
    async (status: string, duration: number, report: ITimetableReportItem) => {
      if (
        (report?.direction_tariff === 'group_per_lesson' ||
          (Number(report?.isOpen) === 1 &&
            (report?.attendees?.length ?? 0) > 0)) &&
        status !== STATUS_RESCHEDULE
      ) {
        setStudentsStatusModal({ report, status })
        return
      }

      const modalRes: {
        resolve: boolean
        value?: {
          comment: string
          date: Dayjs
          startedAt: Dayjs
          endedAt: Dayjs
        }
      } = await new Promise((resolve) => {
        let comment = ''
        let schedule = dayjs().add(1, 'day')
        let startedAt = dayjs(report.start_time, 'HH:mm')
        let endedAt = dayjs(report.end_time, 'HH:mm')

        AModal.confirm({
          title: 'Сохранить отчет',
          content: (
            <SaveContentModal
              report={report}
              status={status}
              setComment={(value) => (comment = value)}
              setSchedule={(value) => (schedule = value)}
              setStartedAt={(value) => (startedAt = value)}
              setEndedAt={(value) => (endedAt = value)}
            />
          ),
          icon: null,
          centered: true,
          maskClosable: true,
          className: `${jclasses.modal} ${classes.confirmModal}`,
          okText: 'Сохранить',
          cancelText: 'Отменить',
          onOk: () => {
            // Для вводного занятия комментарий обязателен при любом статусе
            const isCommentRequired =
              status !== STATUS_DONE ||
              report?.conference_type === STATUS_INTRODUCTION

            if (isCommentRequired && !comment.trim()) {
              addAlert({
                title: 'Нужно написать комментарий',
                type: 'error',
              })
              return Promise.reject()
            }

            return resolve({
              resolve: true,
              value: { comment, date: schedule, startedAt, endedAt },
            })
          },
          onCancel: () => resolve({ resolve: false }),
        })
      })

      if (!modalRes.resolve || !modalRes.value) {
        return
      }

      if (status === STATUS_RESCHEDULE) {
        // Вводное занятие: старое остаётся «Перенесено», новое создаётся отдельно
        const response =
          report?.conference_type === STATUS_INTRODUCTION
            ? await rescheduleIntroductoryLesson(
                report.id,
                modalRes.value.comment,
                modalRes.value.date,
                modalRes.value.startedAt,
                modalRes.value.endedAt,
              )
            : await rescheduleLesson(
                report.id,
                modalRes.value.comment,
                modalRes.value.date,
                modalRes.value.startedAt,
                modalRes.value.endedAt,
              )

        if (response) {
          processedReportsIdsRef.current.add(report.id)
          addAlert({ title: 'Занятие перенесено' })
          removeReportFromQueue(report.id)
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }

        return
      }

      if (status === STATUS_DONE) {
        const response = await makeLessonCompleted(
          report.id,
          modalRes.value.comment,
          report.date_conference,
          duration,
        )

        if (response) {
          processedReportsIdsRef.current.add(report.id)
          addAlert({ title: 'Отчет сохранен' })
          removeReportFromQueue(report.id)
        } else {
          addAlert({
            title: 'Ошибка',
            text: 'Попробуйте ещё раз или обратитесь к администратору',
            type: 'error',
          })
        }

        return
      }

      const response = await makeLessonCancel(
        report.id,
        modalRes.value.comment,
        status,
      )

      if (response) {
        processedReportsIdsRef.current.add(report.id)
        addAlert({ title: 'Урок отменён' })
        removeReportFromQueue(report.id)
      } else {
        addAlert({
          title: 'Ошибка',
          text: 'Попробуйте ещё раз или обратитесь к администратору',
          type: 'error',
        })
      }
    },
    [addAlert, removeReportFromQueue],
  )

  const shouldShowJournalHint = currentReport
    ? isGroupReportBlockedByGrades(currentReport)
    : false
  const journalLink = '/v2/journal'

  return (
    <>
      <Modal
        active={Boolean(currentReport)}
        setActive={() => {}}
        className={`${sharedClasses.modalWrapper} ${classes.modalWrapper}` as any}
      >
        <div className={sharedClasses.modalContainer}>
          <div className={sharedClasses.modalHeader}>
            <div className={sharedClasses.modalTitleContainer}>
              <span className={sharedClasses.modalHeaderTitle}>
                Обязательный отчет по занятию
              </span>
            </div>
          </div>
          <div className={classes.contentWrapper}>
            <div className={sharedClasses.description}>
              Чтобы продолжить работу на платформе, отчитайтесь по занятию.
            </div>
            {shouldShowJournalHint && (
              <div className={sharedClasses.description}>
                Перейдите в элжур и проставьте оценки/статусы всем ученикам, чтобы можно было отметить занятие статусом «Проведено».{' '}
                <Link style={{ color: 'var(--color-text-accent-strong)', textDecoration: 'underline'}} to={journalLink}>Перейти в элжур</Link>
              </div>
            )}
            {currentReport && (
              <div className={classes.cardViewport}>
                <div className={classes.cardInner}>
                  <LessonReportCard
                    key={currentReport.id}
                    report={currentReport}
                    initialForm={reportDrafts[currentReport.id]}
                    onFormChange={(value) => {
                      setReportDrafts((prev) => ({
                        ...prev,
                        [currentReport.id]: value,
                      }))
                    }}
                    onSave={(status, duration) =>
                      saveReport(status, duration, currentReport)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
      {studentsStatusModal && (
        <ChangeStudentsLessonStatusModal
          open={!!studentsStatusModal}
          report={studentsStatusModal.report}
          status={studentsStatusModal.status}
          onDismiss={() => setStudentsStatusModal(null)}
          onSuccess={() => {
            processedReportsIdsRef.current.add(studentsStatusModal.report.id)
            removeReportFromQueue(studentsStatusModal.report.id)
            setStudentsStatusModal(null)
          }}
        />
      )}
    </>
  )
}

export default RequiredLessonReportModal
